import React from 'react';
import type { AuditNoteEntry } from './AuditModal';

/**
 * Orders audit entries newest first. Entries logged in the same minute fall back
 * to the id (which carries a millisecond timestamp) so the order stays stable
 * across renders rather than depending on sort implementation.
 */
function byNewestFirst(first: AuditNoteEntry, second: AuditNoteEntry): number {
  const timestampCompare = `${second.date} ${second.time}`.localeCompare(
    `${first.date} ${first.time}`,
  );
  return timestampCompare !== 0 ? timestampCompare : second.id.localeCompare(first.id);
}

/**
 * Props for the audit history list.
 */
interface AuditHistoryListProps {
  entries: AuditNoteEntry[];
  onEdit: (entry: AuditNoteEntry) => void;
  onDelete: (entry: AuditNoteEntry) => void;
}

/**
 * Audit history covering both auto-logged SYSTEM findings and manual FCP
 * entries, newest first. Renders nothing when there is no history.
 */
const AuditHistoryList: React.FC<AuditHistoryListProps> = ({ entries, onEdit, onDelete }) => {
  if (entries.length === 0) return null;
  return (
    <div className="field-row field-row-tall">
      <label className="field-label">Audit History</label>
      <div className="field-group defect-group">
        {[...entries].sort(byNewestFirst).map((note) => (
          <div key={note.id} className="note-entry">
            <div className="note-entry-header">
              <span className="note-timestamp">
                {note.date} {note.time}z
              </span>
              <span className="note-alias">({note.alias})</span>
              <span className="note-defects">{note.fcpCode}</span>
              <button
                className="barrier-edit-btn audit-entry-action"
                type="button"
                onClick={() => onEdit(note)}
                aria-label="Edit FCP entry"
                title="Edit"
              >
                &#9998;
              </button>
              <button
                className="barrier-delete-btn audit-entry-action"
                type="button"
                onClick={() => onDelete(note)}
                aria-label="Delete FCP entry"
                title="Delete"
              >
                &#128465;
              </button>
            </div>
            <div className="note-entry-text">{note.text}</div>
            {note.editedBy && <div className="note-entry-edited-by">Edited by {note.editedBy}</div>}
          </div>
        ))}
      </div>
    </div>
  );
};

export default AuditHistoryList;
